/* eslint-disable @typescript-eslint/no-unused-expressions */
import type {CaseReducer, PayloadAction} from '@reduxjs/toolkit'

import {generateFreeName} from '../../../../../utils/generateFreeName'

import type {PipelineDraftState} from './EditPipelinePage.slices.types'
import {
  deleteIfEmpty,
  ensurePipelineDraftEntry,
  getPipelineDraftEntry,
} from './EditPipelinePage.slices.utils'

export const updateSecret: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    id: string
    savedName: string
    value: string
    branch: string
  }>
> = (state, action) => {
  const {id, savedName, value, branch} = action.payload

  const {secrets} = getPipelineDraftEntry(state, id, branch)?.draft?.settings ?? {}
  if (secrets?.[savedName] != null) {
    secrets[savedName] = value
  }
}

export const renameSecret: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    id: string
    name: string
    savedName: string
    branch: string
  }>
> = (state, action) => {
  const {id, name, savedName, branch} = action.payload

  if (name !== savedName) {
    const pipeline = ensurePipelineDraftEntry(state, id, branch)
    pipeline.renamed ??= {}
    pipeline.renamed!.secrets ??= {}
    pipeline.renamed!.secrets![savedName] = name
  } else if (getPipelineDraftEntry(state, id, branch)?.renamed?.secrets?.[savedName] != null) {
    const pipeline = getPipelineDraftEntry(state, id, branch)!
    delete pipeline.renamed!.secrets![savedName]
    deleteIfEmpty(pipeline.renamed, 'secrets') && deleteIfEmpty(pipeline, 'renamed')
  }
}

export const deleteSecret: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    id: string
    savedName: string
    branch: string
  }>
> = (state, action) => {
  const {id, savedName, branch} = action.payload

  if (getPipelineDraftEntry(state, id, branch)?.added?.secrets?.[savedName]) {
    const pipeline = getPipelineDraftEntry(state, id, branch)!
    delete pipeline.draft?.settings.secrets?.[savedName]
    delete pipeline.added!.secrets![savedName]
    deleteIfEmpty(pipeline.added, 'secrets') && deleteIfEmpty(pipeline, 'added')
    delete pipeline.renamed!.secrets![savedName]
    deleteIfEmpty(pipeline.renamed, 'secrets') && deleteIfEmpty(pipeline, 'renamed')
  } else {
    const pipeline = ensurePipelineDraftEntry(state, id, branch)
    pipeline.deleted ??= {}
    pipeline.deleted!.secrets ??= []
    pipeline.deleted!.secrets!.push(savedName)
  }
}

export const restoreSecret: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    id: string
    savedName: string
    branch: string
  }>
> = (state, action) => {
  const {id, savedName, branch} = action.payload
  const {deleted} = getPipelineDraftEntry(state, id, branch) ?? {}

  if (deleted?.secrets?.includes(savedName)) {
    deleted.secrets = deleted.secrets.filter(item => item !== savedName)
    deleteIfEmpty(deleted, 'secrets') &&
      deleteIfEmpty(getPipelineDraftEntry(state, id, branch), 'deleted')
  }
}

export const addSecret: CaseReducer<
  PipelineDraftState,
  PayloadAction<{id: string; branch: string}>
> = (state, action) => {
  const {id, branch} = action.payload
  const pipeline = getPipelineDraftEntry(state, id, branch)
  const {draft} = pipeline ?? {}
  if (draft != null) {
    draft.settings.secrets ??= {}
    const key = generateFreeName({
      getName: iteration => `secret_${iteration}`,
      usedNames: Object.keys(draft.settings.secrets),
      errorMessage: 'Cannot generate unique secret key',
    })
    draft.settings.secrets[key] = ''
    renameSecret(state, {type: '', payload: {id, name: '', savedName: key, branch}})
    pipeline!.added ??= {}
    pipeline!.added!.secrets ??= {}
    pipeline!.added!.secrets![key] = true
  }
}

export const clearAddedSecrets: CaseReducer<
  PipelineDraftState,
  PayloadAction<{id: string; branch: string}>
> = (state, action) => {
  const {id, branch} = action.payload
  const {added, draft} = getPipelineDraftEntry(state, id, branch) ?? {}
  if (added?.secrets != null) {
    Object.keys(added.secrets).forEach(key => {
      delete draft?.settings.secrets?.[key]
    })
    delete added.secrets
    deleteIfEmpty(getPipelineDraftEntry(state, id, branch), 'added')
  }
}

export const applyAddedSecrets: CaseReducer<
  PipelineDraftState,
  PayloadAction<{id: string; branch: string}>
> = (state, action) => {
  const {id, branch} = action.payload
  const {added} = getPipelineDraftEntry(state, id, branch) ?? {}
  if (added?.secrets != null) {
    delete added.secrets
    deleteIfEmpty(getPipelineDraftEntry(state, id, branch), 'added')
  }
}
